import { Link } from 'react-router-dom';
import { Building2, ClipboardList, Users, FileText, Receipt, MessageSquareWarning, Calendar, Banknote, Inbox, BookOpen, Bell, UserCircle } from 'lucide-react';
import { api } from '../../lib/api';
import { formatPrice, formatDate } from '../../lib/format';
import { StatCard, Spinner, EmptyState, StatusBadge, Avatar } from '../../components/ui';
import QuickLinks from '../../components/QuickLinks';
import DashboardStatus from '../../components/DashboardStatus';
import { useLivePoll } from '../../lib/useLivePoll';

const QUICK_LINKS = [
  { to: '/manager/staff', label: 'Staff', icon: <Users className="h-5 w-5" />, description: 'My team' },
  { to: '/manager/assignments', label: 'Assignments', icon: <ClipboardList className="h-5 w-5" />, description: 'Team workload' },
  { to: '/manager/reports', label: 'Site Report', icon: <FileText className="h-5 w-5" />, description: 'Reports & forms' },
  { to: '/manager/complaints', label: 'Residents Complaint', icon: <MessageSquareWarning className="h-5 w-5" />, description: 'Resident issues' },
  { to: '/manager/expenses', label: 'Site Expenses', icon: <Receipt className="h-5 w-5" />, description: 'Expense claims' },
  { to: '/manager/bills', label: 'Bills', icon: <Banknote className="h-5 w-5" />, description: 'Site bills' },
  { to: '/manager/meetings', label: 'Meetings', icon: <Calendar className="h-5 w-5" />, description: 'Schedule & records' },
  { to: '/manager/requests', label: 'Request', icon: <Inbox className="h-5 w-5" />, description: 'Staff requests' },
  { to: '/manager/staff-book', label: 'Staff Book', icon: <BookOpen className="h-5 w-5" />, description: 'Staff list' },
  { to: '/manager/notices', label: 'Notices', icon: <Bell className="h-5 w-5" />, description: 'Announcements' },
  { to: '/manager/profile', label: 'My Profile', icon: <UserCircle className="h-5 w-5" />, description: 'Account details' },
];

interface ManagerDashboardData {
  stats: {
    totalProperties: number;
    availableProperties: number;
    totalAssignments: number;
    pendingAssignments: number;
    inProgressAssignments: number;
    completedAssignments: number;
    staffCount: number;
    myReports: number;
    staffReports: number;
    pendingExpenses: number;
    pendingExpenseAmount: number;
    openComplaints: number;
  };
  recentStaff: Array<{ id: number; name: string; role: string; image_url: string | null; role_title: string | null; estate: string | null }>;
  recentComplaints: Array<{ id: number; subject: string; status: string; created_at: string; name: string }>;
  upcomingMeetings: Array<{ id: number; title: string; meeting_date: string; duration: number; meeting_url: string; status: string }>;
}

export default function Dashboard() {
  const { data, loading, refreshing, lastUpdated, reload } = useLivePoll<ManagerDashboardData>(
    () => api.get<ManagerDashboardData>('/dashboard')
  );

  if (loading) return <Spinner label="Loading dashboard..." />;
  if (!data) return <EmptyState message="Could not load dashboard data." />;

  const s = data.stats;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <DashboardStatus lastUpdated={lastUpdated} refreshing={refreshing} onRefresh={reload} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={<Building2 className="h-8 w-8" />}
          label="Available Properties"
          value={s.availableProperties}
          sub={`${s.totalProperties} total`}
        />
        <StatCard
          icon={<ClipboardList className="h-8 w-8" />}
          label="Assignments"
          value={s.totalAssignments}
          color="text-purple-600"
          sub={`${s.pendingAssignments} pending · ${s.inProgressAssignments} in progress · ${s.completedAssignments} completed`}
        />
        <StatCard
          icon={<Users className="h-8 w-8" />}
          label="Staff"
          value={s.staffCount}
          color="text-green-600"
        />
        <StatCard
          icon={<FileText className="h-8 w-8" />}
          label="My Weekly/Monthly Reports"
          value={s.myReports}
          color="text-yellow-600"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={<FileText className="h-8 w-8" />}
          label="Staff Reports"
          value={s.staffReports}
          color="text-purple-600"
        />
        <StatCard
          icon={<Receipt className="h-8 w-8" />}
          label="Pending Expenses"
          value={s.pendingExpenses}
          color="text-yellow-600"
          sub={formatPrice(s.pendingExpenseAmount, 'NGN')}
        />
        <StatCard
          icon={<MessageSquareWarning className="h-8 w-8" />}
          label="Open Complaints"
          value={s.openComplaints}
          color="text-red-600"
        />
        <StatCard
          icon={<Calendar className="h-8 w-8" />}
          label="Upcoming Meetings"
          value={data.upcomingMeetings.length}
          color="text-primary"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4">My Staff</h2>
          {data.recentStaff.length === 0 ? (
            <EmptyState message="No staff members." />
          ) : (
            <div className="space-y-4">
              {data.recentStaff.map((st) => (
                <div key={st.id} className="flex items-center justify-between gap-3 flex-wrap py-2 border-b last:border-b-0">
                  <div className="flex items-center">
                    <Avatar src={st.image_url} name={st.name} className="w-8 h-8 mr-3" />
                    <div>
                      <p className="font-medium">{st.name}</p>
                      <p className="text-xs text-gray-500">{st.role_title || st.role}{st.estate ? ` · ${st.estate}` : ''}</p>
                    </div>
                  </div>
                  <Link to="/manager/staff" className="text-primary text-sm hover:underline">View</Link>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4">Recent Complaints</h2>
          {data.recentComplaints.length === 0 ? (
            <EmptyState message="No complaints." />
          ) : (
            <div className="space-y-4">
              {data.recentComplaints.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 flex-wrap py-2 border-b last:border-b-0">
                  <div>
                    <p className="font-medium">{c.subject}</p>
                    <p className="text-sm text-gray-500">{c.name} · {formatDate(c.created_at)}</p>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-xl font-semibold mb-4">Upcoming Meetings</h2>
        {data.upcomingMeetings.length === 0 ? (
          <EmptyState message="No upcoming meetings." />
        ) : (
          <div className="space-y-4">
            {data.upcomingMeetings.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 flex-wrap py-2 border-b last:border-b-0">
                <div className="flex items-center">
                  <Calendar className="h-5 w-5 text-primary mr-3 shrink-0" />
                  <div>
                    <p className="font-medium">{m.title}</p>
                    <p className="text-sm text-gray-500">{formatDate(m.meeting_date)} · {m.duration} min</p>
                  </div>
                </div>
                <StatusBadge status={m.status} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8">
        <QuickLinks links={QUICK_LINKS} title="All Information" />
      </div>
    </div>
  );
}